import React, { useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Checkbox, Dropdown, Input } from 'antd';
import { useTypedSelector } from '@hooks';
import { TranscribeAssetDto } from '@api/Api';
import { transcribeAssetVersion } from '@redux/actions/assetsAction';
import { transcribeLanguages } from '@helpers/transcribeLanguages';
import { useResponsive } from '@hooks/useResponsive';

import { ReactComponent as DownArrowSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as RightArrowSvg } from '@assets/icons/arrow-right.svg';
import { ReactComponent as GenerateTranscriptionSvg } from '@assets/icons/MediaViewer/generate-transcription.svg';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';

import './CreateTranscript.less';

export default function CreateTranscript() {
  const dispatch = useDispatch();
  const asset = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const { isMdWidth } = useResponsive();
  const actionsRef = useRef<HTMLDivElement>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en-US');
  const [searchTextLanguage, setSearchTextLanguage] = useState('');
  const [isSpeakerLabels, setIsSpeakerLabels] = useState(true);
  const options = useMemo(() => {
    let _items = transcribeLanguages;
    if (searchTextLanguage) {
      _items = _items.filter((language) =>
        language.label.toLowerCase().includes(searchTextLanguage.toLowerCase())
      );
    }
    return _items;
  }, [searchTextLanguage]);

  const transcribeLanguagesLabel = useMemo(() => {
    return (
      transcribeLanguages.find((language) => language.key === selectedLanguage)
        ?.label || selectedLanguage
    );
  }, [selectedLanguage]);

  const onCreateTranscript = () => {
    if (!asset) return;
    if (!selectedVersion) return;
    dispatch(
      transcribeAssetVersion({
        assetId: asset.id,
        assetVersionId: selectedVersion.id,
        language: [selectedLanguage] as TranscribeAssetDto['language'],
        speakerLabels: isSpeakerLabels
      })
    );
  };

  return (
    <div className="media_viewer_create-transcript">
      <div className="media_viewer_create-transcript-icon">
        <GenerateTranscriptionSvg />
      </div>
      <div className="media_viewer_create-transcript-title">
        Generate transcription
      </div>
      <div className="media_viewer_create-transcript-text">
        Create a transcript and captions for your asset to make navigation and
        feedback easier.
      </div>
      <div ref={actionsRef} className="media_viewer_create-transcript-actions">
        <Dropdown
          getPopupContainer={() => actionsRef.current || document.body}
          menu={{
            selectable: true,
            items: [
              {
                key: 'languageGroupHead',
                label: (
                  <Input
                    prefix={<SearchSvg />}
                    placeholder="Search"
                    value={searchTextLanguage}
                    className="media_viewer_create-transcript-language-search"
                    onChange={(e) => setSearchTextLanguage(e.target.value)}
                  />
                ),
                type: 'group'
              },
              {
                key: 'languageGroup',
                type: 'group',
                className: 'media_viewer_create-transcript-language-menu',
                children: [
                  ...options,
                  ...(isMdWidth
                    ? [
                        {
                          key: 'cancel',
                          label: 'Cancel'
                        }
                      ]
                    : [])
                ]
              }
            ],
            selectedKeys: [selectedLanguage],
            onSelect: ({ key }) => {
              if (key.indexOf('cancel') < 0) {
                setSelectedLanguage(key);
              }
            }
          }}
          prefixCls="media_viewer_menu"
          overlayClassName="media_viewer_create-transcript-menu media_viewer_mobile-overlay"
          placement="bottomLeft"
          trigger={['click']}
        >
          <Button
            className="media_viewer_btn media_viewer_create-transcript-language-btn"
            icon={<DownArrowSvg />}
          >
            {transcribeLanguagesLabel}
          </Button>
        </Dropdown>
        <Button
          size="small"
          type="primary"
          icon={<RightArrowSvg width={24} height={24} />}
          className="media_viewer_create-transcript-transcribe-btn"
          onClick={onCreateTranscript}
        >
          Transcribe
        </Button>
      </div>
      <Checkbox
        onChange={(e) => {
          setIsSpeakerLabels(e.target.checked);
        }}
        checked={isSpeakerLabels}
        className="media_viewer_create-transcript-speaker-labels-checkbox"
      >
        Identify speakers
      </Checkbox>
    </div>
  );
}
